import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Notification } from '@/types'

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<Notification[]>([])

  const add = (notification: Omit<Notification, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
    const duration = notification.duration ?? 5000
    const newNotification: Notification = {
      ...notification,
      id,
      duration,
    }
    
    notifications.value.push(newNotification)

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        remove(id)
      }, duration)
    }

    return id
  }

  const remove = (id: string) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  const clear = () => {
    notifications.value = []
  }

  // Convenience methods
  const success = (title: string, message?: string) => {
    return add({ type: 'success', title, message })
  }

  const error = (title: string, message?: string) => {
    return add({ type: 'error', title, message, duration: 8000 })
  }

  const warning = (title: string, message?: string) => {
    return add({ type: 'warning', title, message })
  }

  const info = (title: string, message?: string) => {
    return add({ type: 'info', title, message })
  }

  // Copy-specific helper for password vault
  const copied = (field: string) => {
    const messages: Record<string, string> = {
      password: 'Password copied to clipboard',
      username: 'Username copied to clipboard',
      totp: 'TOTP code copied to clipboard',
      url: 'URL copied to clipboard',
      note: 'Note copied to clipboard',
      link: 'Link copied to clipboard',
    }
    return add({ type: 'success', title: messages[field] || `${field} copied to clipboard`, duration: 2000 })
  }

  return {
    notifications,
    add,
    remove,
    clear,
    success,
    error,
    warning,
    info,
    copied,
  }
})
